import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Amenity name formatting
// ============================================================================

const MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into',
  'nor', 'of', 'on', 'onto', 'or', 'per', 'the', 'to', 'via', 'vs', 'with',
]);

const KNOWN_CASING: Record<string, string> = {
  ...Object.fromEntries(
    [
      'TV', 'TVs', 'HDTV', 'RV', 'RVs', 'EV', 'EVs', 'ATV', 'AC', 'A/C', 'HVAC', 'USB', 'LED',
      'DVD', 'BBQ', 'BBQs', 'ADA', 'ATM', 'HD', 'UV', 'GE', 'HOA', 'NFC', 'DIY', 'W/D', 'Wi-Fi',
      'II', 'III', 'IV', 'PetSmart', 'ChargePoint',
    ].map(word => [word.toLowerCase(), word]),
  ),
  wifi: 'Wi-Fi',
};

function splitPunctuation(word: string): [string, string, string] {
  const match = word.match(/^([^A-Za-z0-9]*)(.*?)([^A-Za-z0-9]*)$/);
  return match ? [match[1], match[2], match[3]] : ['', word, ''];
}

function capitalizePart(part: string): string {
  const [lead, core, trail] = splitPunctuation(part);
  const known = KNOWN_CASING[core.toLowerCase()];
  if (known) return lead + known + trail;
  // Mixed-case brand casing from the source (e.g. iPhone, LuxerOne) is left untouched.
  if (/[a-z]/.test(core) && /[A-Z]/.test(core.slice(1))) return part;
  return lead + core.toLowerCase().replace(/^([a-z])/, ch => ch.toUpperCase()) + trail;
}

function formatWord(word: string): string {
  const [lead, core, trail] = splitPunctuation(word);
  const known = KNOWN_CASING[core.toLowerCase()];
  if (known) return lead + known + trail;
  return word.split(/([-/])/).map(capitalizePart).join('');
}

function toTitleCase(text: string): string {
  const words = text.split(' ');
  return words
    .map((word, i) => {
      const isEdge = i === 0 || i === words.length - 1;
      if (!isEdge && MINOR_WORDS.has(word.toLowerCase())) return word.toLowerCase();
      return formatWord(word);
    })
    .join(' ');
}

function hasAsterisk(title: string | null | undefined): boolean {
  return !!title && title.includes('*');
}

function formatAmenityName(title: string | null | undefined): string {
  if (!title) return '';
  const name = toTitleCase(title.replace(/\*/g, '').replace(/\s+/g, ' ').trim());
  return hasAsterisk(title) ? `${name}*` : name;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface SlideData {
  image: string;
  alt: string;
}

interface Slide {
  key: string;
  node: ReactNode;
}

interface SlotPhoto {
  image?: string | null;
  alt?: string | null;
}

// Generated slides win as a set. Until any slide has been generated (or set by
// hand), each slide falls back to the filtered gallery photo at its index so
// existing sites keep rendering the same photos.
function resolveSlides(slots: SlotPhoto[], fallback: GalleryPhoto[], heading: string): (SlideData | null)[] {
  const useSlots = slots.some(slot => slot.image);
  const seen = new Set<string>();
  return slots.map((slot, i) => {
    const image = useSlots ? slot.image : fallback[i]?.image;
    if (!image || seen.has(image)) return null;
    seen.add(image);
    const alt = useSlots ? slot.alt : fallback[i]?.alt_text || fallback[i]?.title;
    return { image, alt: alt || heading };
  });
}

function SlideImage({ slide, priority = false }: { slide: SlideData; priority?: boolean }) {
  return (
    <VibesitesImage
      url={slide.image}
      alt={slide.alt}
      className="amenities-slider-img"
      sizes="(min-width: 768px) 520px, 100vw"
      priority={priority}
    />
  );
}

interface PhotoSliderProps {
  slides: Slide[];
}

function PhotoSlider({ slides }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = slides.length;

  useEffect(() => {
    if (current >= count && count > 0) setCurrent(0);
  }, [current, count]);

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.key}
          className={`amenities-slide${i === current ? ' amenities-slide--active' : ''}`}
          aria-hidden={i === current ? undefined : true}
        >
          {slide.node}
        </div>
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Column component
// ============================================================================

const PAGE_SIZE = 10;

interface AmenityColumnProps {
  headingNode: ReactNode;
  items: Amenity[];
  slides: Slide[];
  footnote?: ReactNode;
}

function AmenityColumn({ headingNode, items, slides, footnote }: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const listRef = useRef<HTMLUListElement>(null);
  const [page, setPage] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const hasPagination = totalPages > 1;

  const goToPage = useCallback((next: number) => {
    if (next === page || transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setPage(next);
      setTimeout(() => setTransitioning(false), 30);
    }, 250);
  }, [page, transitioning]);

  return (
    <div ref={colRef} className="reveal-fade-up amenities-col">
      {/* Photo slider */}
      {slides.length > 0 && <PhotoSlider slides={slides} />}

      {/* Heading */}
      {headingNode}

      {/* Items */}
      <ul
        ref={listRef}
        className={`amenities-col-list amenities-page-transition${transitioning ? ' amenities-page-out' : ''}`}
      >
        {pageItems.map((item, i) => (
          <li key={`${page}-${i}`} className="amenities-col-item">
            <span className="amenities-col-bullet" aria-hidden="true" />
            <span>{formatAmenityName(item.title)}</span>
          </li>
        ))}
      </ul>

      {footnote}

      {/* Pagination */}
      {hasPagination && (
        <div className="amenities-pagination">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 0 || transitioning}
            className="amenities-page-btn"
            aria-label="Previous page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="amenities-page-dots">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                disabled={transitioning}
                className={`amenities-page-dot${i === page ? ' amenities-page-dot--active' : ''}`}
                aria-label={`Page ${i + 1}`}
                aria-current={i === page ? 'page' : undefined}
              />
            ))}
          </div>

          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages - 1 || transitioning}
            className="amenities-page-btn"
            aria-label="Next page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Slider photo slot groups (literal objects for contentSchema extraction)
// ============================================================================

// No defaultField: an ungenerated slide resolves empty so the slider can fall
// back to the filtered gallery collection (see resolveSlides).

const communityPhoto1Def: SlotGroupDefinition = {
  label: 'Community Photo 1',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The single most desirable shared community amenity at this property — the space a prospective resident would be most excited to see — in a sharp, well-lit photo. Not a plain building exterior, an apartment interior, a map, a logo, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 1 of 6 (lead slide, seen first) in the Community Amenities photo slider on the Amenities page, above the community amenities list; card-sized landscape crop (~520×300). Slides run from most to least desirable amenity, each a different amenity space from slides 2–6. Prefer a different photo from the Amenities page hero banner. Use an exterior or grounds photo only if the property has no community amenity photos at all.',
  },
};

const communityPhoto2Def: SlotGroupDefinition = {
  label: 'Community Photo 2',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The next most desirable shared community amenity at this property after slide 1, in a sharp, well-lit photo. Not a plain building exterior, an apartment interior, a map, a logo, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 2 of 6 in the Community Amenities photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable amenity. Must be a different amenity and photo from slides 1 and 3–6. Leave empty rather than repeat a photo or fall back to an exterior shot.',
  },
};

const communityPhoto3Def: SlotGroupDefinition = {
  label: 'Community Photo 3',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The third most desirable shared community amenity at this property, not shown on slides 1 or 2, in a sharp, well-lit photo. Not a plain building exterior, an apartment interior, a map, a logo, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 3 of 6 in the Community Amenities photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable amenity. Must be a different amenity and photo from slides 1, 2, and 4–6. Leave empty rather than repeat a photo or fall back to an exterior shot.',
  },
};

const communityPhoto4Def: SlotGroupDefinition = {
  label: 'Community Photo 4',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The fourth most desirable shared community amenity at this property, not yet covered by slides 1–3 — indoor or outdoor. Not a plain building exterior, an apartment interior, a map, a logo, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 4 of 6 in the Community Amenities photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable amenity. Must be a different amenity and photo from slides 1–3, 5, and 6. Leave empty rather than repeat a photo or fall back to an exterior shot.',
  },
};

const communityPhoto5Def: SlotGroupDefinition = {
  label: 'Community Photo 5',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The fifth most desirable shared community amenity at this property, not shown on slides 1–4 — indoor or outdoor. Not a plain building exterior, an apartment interior, a map, a logo, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 5 of 6 in the Community Amenities photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable amenity. Must be a different amenity and photo from slides 1–4 and 6. Leave empty rather than repeat a photo or fall back to an exterior shot.',
  },
};

const communityPhoto6Def: SlotGroupDefinition = {
  label: 'Community Photo 6',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'One more community amenity photo at this property that rounds out the set — a shared space not shown on slides 1–5, or a distinct angle of the grounds or common areas if every amenity is already covered. Not an apartment interior, a map, a logo, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 6 of 6 (last) in the Community Amenities photo slider on the Amenities page; card-sized landscape crop. Must be a different photo from slides 1–5. Leave empty rather than repeat a photo.',
  },
};

const apartmentPhoto1Def: SlotGroupDefinition = {
  label: 'Apartment Photo 1',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The single most desirable in-unit photo at this property — the room or in-home feature that best sells the apartment — sharp and well lit. Not a shared amenity, the building exterior, a floor plan diagram, a map, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 1 of 6 (lead slide, seen first) in the Apartment Features photo slider on the Amenities page, above the apartment features list; card-sized landscape crop (~520×300). Slides run from most to least desirable, each a different room or in-home feature from slides 2–6.',
  },
};

const apartmentPhoto2Def: SlotGroupDefinition = {
  label: 'Apartment Photo 2',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The next most desirable in-unit photo at this property after slide 1, showing a different room or feature. Not a shared amenity, the building exterior, a floor plan diagram, a map, or a graphic. Alt text describes the chosen photo.',
    context:
      'Slide 2 of 6 in the Apartment Features photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable. Must be a different room and photo from slides 1 and 3–6. Leave empty rather than repeat a photo.',
  },
};

const apartmentPhoto3Def: SlotGroupDefinition = {
  label: 'Apartment Photo 3',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The third most desirable in-unit photo at this property, of a room or in-home feature not shown on slides 1 or 2 — ideally one that illustrates an item in the apartment features list. Not a shared amenity or the building exterior. Alt text describes the chosen photo.',
    context:
      'Slide 3 of 6 in the Apartment Features photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable. Must be a different room and photo from slides 1, 2, and 4–6. Leave empty rather than repeat a photo.',
  },
};

const apartmentPhoto4Def: SlotGroupDefinition = {
  label: 'Apartment Photo 4',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The fourth most desirable in-unit or private outdoor-space photo at this property, not yet covered by slides 1–3. Not a shared amenity or the building exterior. Alt text describes the chosen photo.',
    context:
      'Slide 4 of 6 in the Apartment Features photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable. Must be a different room and photo from slides 1–3, 5, and 6. Leave empty rather than repeat a photo.',
  },
};

const apartmentPhoto5Def: SlotGroupDefinition = {
  label: 'Apartment Photo 5',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'The fifth most desirable in-unit photo at this property — a room, private outdoor space, or in-home feature not shown on slides 1–4. Not a shared amenity or the building exterior. Alt text describes the chosen photo.',
    context:
      'Slide 5 of 6 in the Apartment Features photo slider on the Amenities page; card-sized landscape crop. Slides run from most to least desirable. Must be a different room and photo from slides 1–4 and 6. Leave empty rather than repeat a photo.',
  },
};

const apartmentPhoto6Def: SlotGroupDefinition = {
  label: 'Apartment Photo 6',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'One more in-unit photo at this property that rounds out the set — a room, finish detail, or view not shown on slides 1–5. Not a shared amenity or the building exterior. Alt text describes the chosen photo.',
    context:
      'Slide 6 of 6 (last) in the Apartment Features photo slider on the Amenities page; card-sized landscape crop. Must be a different photo from slides 1–5. Leave empty rather than repeat a photo.',
  },
};

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.communityHeading');
  const apartmentHeading = useContentToken('amenitieslist.apartmentHeading');
  const apartmentDisclaimer = useContentToken('amenitieslist.apartmentDisclaimer');

  const communityPhoto1 = useSlotGroup('amenitieslist.community_photo1', communityPhoto1Def);
  const communityPhoto2 = useSlotGroup('amenitieslist.community_photo2', communityPhoto2Def);
  const communityPhoto3 = useSlotGroup('amenitieslist.community_photo3', communityPhoto3Def);
  const communityPhoto4 = useSlotGroup('amenitieslist.community_photo4', communityPhoto4Def);
  const communityPhoto5 = useSlotGroup('amenitieslist.community_photo5', communityPhoto5Def);
  const communityPhoto6 = useSlotGroup('amenitieslist.community_photo6', communityPhoto6Def);
  const apartmentPhoto1 = useSlotGroup('amenitieslist.apartment_photo1', apartmentPhoto1Def);
  const apartmentPhoto2 = useSlotGroup('amenitieslist.apartment_photo2', apartmentPhoto2Def);
  const apartmentPhoto3 = useSlotGroup('amenitieslist.apartment_photo3', apartmentPhoto3Def);
  const apartmentPhoto4 = useSlotGroup('amenitieslist.apartment_photo4', apartmentPhoto4Def);
  const apartmentPhoto5 = useSlotGroup('amenitieslist.apartment_photo5', apartmentPhoto5Def);
  const apartmentPhoto6 = useSlotGroup('amenitieslist.apartment_photo6', apartmentPhoto6Def);

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 6,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 6,
  });

  useRevealStyles();

  const communityHeadingText = communityHeading || 'Community Amenities';
  const apartmentHeadingText = apartmentHeading || 'Apartment Features';

  const [cs1, cs2, cs3, cs4, cs5, cs6] = resolveSlides(
    [communityPhoto1, communityPhoto2, communityPhoto3, communityPhoto4, communityPhoto5, communityPhoto6],
    communityPhotos,
    communityHeadingText,
  );
  const communitySlides = ([] as (Slide | null)[]).concat(
    cs1 && { key: 'community_photo1', node: <EditableSlotGroup groupPath="amenitieslist.community_photo1" definition={communityPhoto1Def} className="amenities-slide-edit"><SlideImage slide={cs1} priority /></EditableSlotGroup> },
    cs2 && { key: 'community_photo2', node: <EditableSlotGroup groupPath="amenitieslist.community_photo2" definition={communityPhoto2Def} className="amenities-slide-edit"><SlideImage slide={cs2} /></EditableSlotGroup> },
    cs3 && { key: 'community_photo3', node: <EditableSlotGroup groupPath="amenitieslist.community_photo3" definition={communityPhoto3Def} className="amenities-slide-edit"><SlideImage slide={cs3} /></EditableSlotGroup> },
    cs4 && { key: 'community_photo4', node: <EditableSlotGroup groupPath="amenitieslist.community_photo4" definition={communityPhoto4Def} className="amenities-slide-edit"><SlideImage slide={cs4} /></EditableSlotGroup> },
    cs5 && { key: 'community_photo5', node: <EditableSlotGroup groupPath="amenitieslist.community_photo5" definition={communityPhoto5Def} className="amenities-slide-edit"><SlideImage slide={cs5} /></EditableSlotGroup> },
    cs6 && { key: 'community_photo6', node: <EditableSlotGroup groupPath="amenitieslist.community_photo6" definition={communityPhoto6Def} className="amenities-slide-edit"><SlideImage slide={cs6} /></EditableSlotGroup> },
  ).filter((slide): slide is Slide => !!slide);

  const [as1, as2, as3, as4, as5, as6] = resolveSlides(
    [apartmentPhoto1, apartmentPhoto2, apartmentPhoto3, apartmentPhoto4, apartmentPhoto5, apartmentPhoto6],
    apartmentPhotos,
    apartmentHeadingText,
  );
  const apartmentSlides = ([] as (Slide | null)[]).concat(
    as1 && { key: 'apartment_photo1', node: <EditableSlotGroup groupPath="amenitieslist.apartment_photo1" definition={apartmentPhoto1Def} className="amenities-slide-edit"><SlideImage slide={as1} priority /></EditableSlotGroup> },
    as2 && { key: 'apartment_photo2', node: <EditableSlotGroup groupPath="amenitieslist.apartment_photo2" definition={apartmentPhoto2Def} className="amenities-slide-edit"><SlideImage slide={as2} /></EditableSlotGroup> },
    as3 && { key: 'apartment_photo3', node: <EditableSlotGroup groupPath="amenitieslist.apartment_photo3" definition={apartmentPhoto3Def} className="amenities-slide-edit"><SlideImage slide={as3} /></EditableSlotGroup> },
    as4 && { key: 'apartment_photo4', node: <EditableSlotGroup groupPath="amenitieslist.apartment_photo4" definition={apartmentPhoto4Def} className="amenities-slide-edit"><SlideImage slide={as4} /></EditableSlotGroup> },
    as5 && { key: 'apartment_photo5', node: <EditableSlotGroup groupPath="amenitieslist.apartment_photo5" definition={apartmentPhoto5Def} className="amenities-slide-edit"><SlideImage slide={as5} /></EditableSlotGroup> },
    as6 && { key: 'apartment_photo6', node: <EditableSlotGroup groupPath="amenitieslist.apartment_photo6" definition={apartmentPhoto6Def} className="amenities-slide-edit"><SlideImage slide={as6} /></EditableSlotGroup> },
  ).filter((slide): slide is Slide => !!slide);

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;
  const showApartmentDisclaimer = apartmentItems.some(item => hasAsterisk(item.title));

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="amenities-grid">
            {communityItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <EditableToken
                    path="amenitieslist.communityHeading"
                    as="h3"
                    className="amenities-col-heading"
                  >
                    {communityHeadingText}
                  </EditableToken>
                }
                items={communityItems}
                slides={communitySlides}
              />
            )}
            {apartmentItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <EditableToken
                    path="amenitieslist.apartmentHeading"
                    as="h3"
                    className="amenities-col-heading"
                  >
                    {apartmentHeadingText}
                  </EditableToken>
                }
                items={apartmentItems}
                slides={apartmentSlides}
                footnote={
                  showApartmentDisclaimer && (
                    <EditableToken
                      path="amenitieslist.apartmentDisclaimer"
                      as="p"
                      className="amenities-col-disclaimer"
                    >
                      {apartmentDisclaimer || '*In Select Units'}
                    </EditableToken>
                  )
                }
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .amenities-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }
        @media (max-width: 768px) {
          .amenities-grid {
            grid-template-columns: 1fr;
          }
        }

        .amenities-col {
          background: white;
          border: 1px solid var(--vibe-color-border);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 300px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .amenities-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slide--active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }
        .amenities-slide-edit {
          display: block;
          width: 100%;
          height: 100%;
        }
        .amenities-slider-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 10px; }
        .amenities-slider-arrow--next { right: 10px; }

        .amenities-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        .amenities-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slide {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: var(--vibe-font-weight-normal);
          color: var(--vibe-color-text);
          text-align: center;
          padding: 1.25rem 1.5rem 0;
          margin: 0;
          letter-spacing: 0.02em;
        }

        .amenities-col-list {
          list-style: none;
          padding: 1rem 1.75rem 1.5rem;
          margin: 0;
          columns: 1;
          flex: 1;
        }
        @media (min-width: 640px) {
          .amenities-col-list {
            columns: 1;
          }
        }

        .amenities-col-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.3rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          color: var(--vibe-color-text);
          break-inside: avoid;
          line-height: 1.5;
        }

        .amenities-col-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        .amenities-col-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
          padding: 0 1.75rem 1.25rem;
          margin: -0.75rem 0 0;
        }

        /* Page transition */
        .amenities-page-transition {
          transition: opacity 0.25s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
          opacity: 1;
          transform: translateY(0);
        }
        .amenities-page-out {
          opacity: 0;
          transform: translateY(8px);
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-page-transition {
            transition: none;
          }
        }

        /* Pagination */
        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem 1.25rem;
        }

        .amenities-page-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s, background 0.2s;
        }
        .amenities-page-btn:hover:not(:disabled) {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }
        .amenities-page-btn:disabled {
          opacity: 0.3;
          cursor: default;
        }

        .amenities-page-dots {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .amenities-page-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid var(--vibe-color-border);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .amenities-page-dot:hover {
          border-color: var(--vibe-color-primary);
          transform: scale(1.2);
        }
        .amenities-page-dot--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'text',
      defaultValue: 'Apartment Features',
    },
    'amenitieslist.apartmentDisclaimer': {
      label: 'Apartment Disclaimer',
      type: 'text',
      defaultValue: '*In Select Units',
    },
  },
  slotGroups: {
    'amenitieslist.community_photo1': communityPhoto1Def,
    'amenitieslist.community_photo2': communityPhoto2Def,
    'amenitieslist.community_photo3': communityPhoto3Def,
    'amenitieslist.community_photo4': communityPhoto4Def,
    'amenitieslist.community_photo5': communityPhoto5Def,
    'amenitieslist.community_photo6': communityPhoto6Def,
    'amenitieslist.apartment_photo1': apartmentPhoto1Def,
    'amenitieslist.apartment_photo2': apartmentPhoto2Def,
    'amenitieslist.apartment_photo3': apartmentPhoto3Def,
    'amenitieslist.apartment_photo4': apartmentPhoto4Def,
    'amenitieslist.apartment_photo5': apartmentPhoto5Def,
    'amenitieslist.apartment_photo6': apartmentPhoto6Def,
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos (Fallback)',
      description: 'Shown in the community slider until its photo slots are generated or set.',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos (Fallback)',
      description: 'Shown in the apartment slider until its photo slots are generated or set.',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
